import { FilterSelect } from '@/components/ui/filters/filter-select'
import { FormField } from '@/components/ui/form-field'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import useUploadFile from '@/hooks/useUploadFile'
import {
    checkPartnerContractNumber,
    createContractPartner,
    getSupplierOptions,
    updateContractPartner,
} from '@/modules/Contract/modules/Partner/services'
import {
    PartnerContract,
    PartnerContractCreateParams,
} from '@/modules/Contract/modules/Partner/types'
import { STORGE_PREFIXS } from '@/modules/Contract/modules/Process/constants'
import { Attachment } from '@/modules/Upload/types'
import { ModalProps } from '@/types/shared'
import { isAttachment, renderFileItem } from '@/utils/function'
import { yupResolver } from '@hookform/resolvers/yup'
import { useQueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'
import { CheckCircle2, Loader2, Plus, XCircle } from 'lucide-react'
import { useCallback, useMemo, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Button, Dialog, FormItem } from 'vnetwork-ui'
import { schemaCreatePartnerContractModal } from './schema'
import {
    QuickCreateSupplier,
    QuickCreateSupplierRef,
} from './QuickCreateSupplier'

export interface CreatePartnerContractModalProps extends ModalProps {
    partnerContract?: PartnerContract
    mode?: 'create' | 'update'
    defaultPartnerId?: number
}

const CreatePartnerContractModal = ({
    onClose,
    open,
    partnerContract,
    mode = 'create',
    defaultPartnerId,
}: CreatePartnerContractModalProps) => {
    const { t } = useTranslation('contract')
    const queryClient = useQueryClient()

    // Contract number validation
    const [isCheckingNumber, setIsCheckingNumber] = useState(false)
    const [numberExists, setNumberExists] = useState<boolean | null>(null)
    const [showQuickCreate, setShowQuickCreate] = useState(false)

    // Ref for quick create supplier
    const quickCreateRef = useRef<QuickCreateSupplierRef>(null)

    // Supplier options
    const { data: supplierOptionsData, refetch: refetchSuppliers } =
        useQueryWithHandler({
            queryKey: [QUERY_KEYS.SUPPLIERS, 'options'],
            queryFn: getSupplierOptions,
        })

    // Include current partner as fallback option when in update mode
    const supplierOptions = useMemo(() => {
        const options = supplierOptionsData || []

        // In update mode, ensure current partner is in options (for immediate display while loading)
        if (
            mode === 'update' &&
            partnerContract?.partner_id &&
            partnerContract?.partner_name
        ) {
            const hasCurrentPartner = options.some(
                (o) => o.partner_id === partnerContract.partner_id
            )
            if (!hasCurrentPartner) {
                return [
                    {
                        partner_id: partnerContract.partner_id,
                        partner_name: partnerContract.partner_name,
                        partner_code: partnerContract.partner_code || '',
                    },
                    ...options,
                ]
            }
        }

        return options
    }, [supplierOptionsData, mode, partnerContract])

    const {
        control,
        handleSubmit,
        getValues,
        setValue,
        setError,
        clearErrors,
        formState: { errors },
    } = useForm<
        PartnerContractCreateParams & { attachments: File[] | Attachment[] }
    >({
        defaultValues: {
            contract_number_id: partnerContract?.contract_number_id || '',
            contract_key: partnerContract?.contract_key || '',
            contract_note: partnerContract?.contract_note || '',
            contract_effective_date: partnerContract?.contract_effective_date
                ? dayjs(partnerContract.contract_effective_date).toDate()
                : null,
            contract_expiry_date: partnerContract?.contract_expiry_date
                ? dayjs(partnerContract.contract_expiry_date).toDate()
                : null,
            contract_value: partnerContract?.contract_value || 0,
            partner_id:
                partnerContract?.partner_id || defaultPartnerId || undefined,
            attachments: partnerContract?.attachments || [],
        },
        resolver: yupResolver(schemaCreatePartnerContractModal as any),
    })

    // Check contract number on blur
    const handleCheckContractNumber = useCallback(async () => {
        const contractNumberId = getValues('contract_number_id')
        if (!contractNumberId?.trim()) {
            setNumberExists(null)
            return
        }
        // Skip check if same as original in update mode
        if (
            mode === 'update' &&
            contractNumberId === partnerContract?.contract_number_id
        ) {
            setNumberExists(false)
            clearErrors('contract_number_id')
            return
        }
        setIsCheckingNumber(true)
        try {
            const result = await checkPartnerContractNumber({
                contract_number_id: contractNumberId,
                contract_id:
                    mode === 'update'
                        ? partnerContract?.contract_id
                        : undefined,
            })
            setNumberExists(result.exists)
            if (result.exists) {
                setError('contract_number_id', {
                    type: 'manual',
                    message: t('contract-number-already-exists'),
                })
            } else {
                clearErrors('contract_number_id')
            }
        } catch {
            setNumberExists(null)
        } finally {
            setIsCheckingNumber(false)
        }
    }, [getValues, mode, partnerContract, setError, clearErrors, t])

    // Mutations
    const { mutateAsync: create, isPending } = useMutationWithHandler(
        { mutationFn: createContractPartner },
        {
            reactQueryKeys: QUERY_KEYS.CONTRACTS,
            successMessage: t('create-partner-contract-success'),
            onSuccess: () => {
                onClose()
                queryClient.refetchQueries({
                    queryKey: [QUERY_KEYS.CONTRACTS_PARTNERS],
                })
            },
        }
    )

    const { mutateAsync: update, isPending: updatePending } =
        useMutationWithHandler(
            { mutationFn: updateContractPartner },
            {
                reactQueryKeys: QUERY_KEYS.CONTRACTS,
                successMessage: t('update-partner-contract-success'),
                onSuccess: () => {
                    onClose()
                    queryClient.refetchQueries({
                        queryKey: [QUERY_KEYS.CONTRACTS_PARTNERS],
                    })
                },
            }
        )

    const { uploadAsync } = useUploadFile({ prefix: STORGE_PREFIXS.PARTNER })

    // Submit handler
    const onSubmit = async (
        values: PartnerContractCreateParams & {
            attachments: File[] | Attachment[]
        }
    ) => {
        if (numberExists) {
            setError('contract_number_id', {
                type: 'manual',
                message: t('contract-number-already-exists'),
            })
            return
        }

        let partnerId = values.partner_id

        // If creating new supplier, create it first
        if (showQuickCreate) {
            if (!quickCreateRef.current?.isValid()) {
                // Validation failed - don't proceed
                return
            }
            const newPartnerId = await quickCreateRef.current.createAndGetId()
            if (!newPartnerId) {
                // Creation failed
                return
            }
            partnerId = newPartnerId
            setShowQuickCreate(false)
        } else if (!partnerId) {
            // No supplier selected and not creating new one
            setError('partner_id', {
                type: 'manual',
                message: t('partner-required'),
            })
            return
        }

        // Handle attachments
        let attachments: Attachment[] = []
        let attachmentsToDelete: number[] = []
        if (values?.attachments) {
            const keptAttachments = values.attachments.filter(
                (a) => !(a instanceof File)
            ) as Attachment[]
            const keptIds = keptAttachments.map((a) => a.attachment_id)
            const newFiles = values.attachments.filter(
                (a) => a instanceof File
            ) as File[]
            const uploaded = await uploadAsync(newFiles)
            const uploadedAttachments = uploaded
                ?.filter((a) => a.status === 'fulfilled')
                .map((a) => a.value) as Attachment[]
            attachments = [...keptAttachments, ...uploadedAttachments]
            const originalIds =
                partnerContract?.attachments?.map((a) => a.attachment_id) || []
            attachmentsToDelete = originalIds.filter(
                (id) => !keptIds.includes(id)
            )
        }

        // Prepare data
        const { attachments: _, ...rest } = values as any
        const data: PartnerContractCreateParams = {
            contract_number_id: rest.contract_number_id,
            partner_id: partnerId,
            ...(rest.contract_key && { contract_key: rest.contract_key }),
            ...(rest.contract_note && { contract_note: rest.contract_note }),
            ...(rest.contract_value && { contract_value: rest.contract_value }),
            ...(rest.contract_effective_date && {
                contract_effective_date: dayjs(
                    rest.contract_effective_date
                ).format('YYYY-MM-DD'),
            }),
            ...(rest.contract_expiry_date && {
                contract_expiry_date: dayjs(rest.contract_expiry_date).format(
                    'YYYY-MM-DD'
                ),
            }),
        }

        mode === 'update' && partnerContract
            ? await update({
                  data,
                  attachments,
                  attachments_to_delete: attachmentsToDelete,
                  contractID: partnerContract.contract_id,
              })
            : await create({ data, attachments })
    }

    // Delete attachment handler
    const onDelete = (file: File | Attachment) => {
        const attachments = getValues('attachments')
        if (file instanceof File) {
            setValue(
                'attachments',
                attachments?.filter(
                    (a) => !(a instanceof File) || a.name !== file.name
                ) as File[]
            )
        } else if (isAttachment(file)) {
            setValue(
                'attachments',
                attachments?.filter(
                    (a) =>
                        a instanceof File ||
                        a.attachment_id !== file.attachment_id
                ) as Attachment[]
            )
        }
    }

    const isLoading = isPending || updatePending

    // Render number validation status
    const renderNumberStatus = () => {
        if (isCheckingNumber)
            return <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
        if (numberExists === true)
            return <XCircle className="w-4 h-4 text-red-500" />
        if (numberExists === false)
            return <CheckCircle2 className="w-4 h-4 text-green-500" />
        return null
    }

    return (
        <Dialog
            title={t(`${mode}-partner-contract`)}
            onClose={onClose}
            open={open}
            maxWidth="md"
        >
            <form onSubmit={handleSubmit(onSubmit)}>
                <div className="grid grid-cols-4 gap-4">
                    {/* Supplier Select */}
                    <div className="col-span-4">
                        {!showQuickCreate ? (
                            <div>
                                <FilterSelect
                                    control={control}
                                    name="partner_id"
                                    label={t('supplier')}
                                    placeholder={t('select-supplier')}
                                    searchPlaceholder={t('search-supplier')}
                                    emptyText={t('no-supplier-found')}
                                    options={supplierOptions.map((opt) => ({
                                        label: `${opt.partner_code} - ${opt.partner_name}`,
                                        value: opt.partner_id,
                                    }))}
                                    className={
                                        errors.partner_id
                                            ? 'border-red-500'
                                            : ''
                                    }
                                    footerItems={[
                                        {
                                            key: 'create-new',
                                            label: t('create-new-supplier'),
                                            icon: <Plus className="w-4 h-4" />,
                                            onClick: (setOpen) => {
                                                setOpen(false)
                                                setShowQuickCreate(true)
                                            },
                                        },
                                    ]}
                                />
                                {errors.partner_id && (
                                    <p className="text-xs text-red-500 mt-1">
                                        {t('partner-required')}
                                    </p>
                                )}
                            </div>
                        ) : (
                            <QuickCreateSupplier
                                ref={quickCreateRef}
                                onCancel={() => setShowQuickCreate(false)}
                                refetchSuppliers={refetchSuppliers}
                            />
                        )}
                    </div>

                    {/* Contract Number */}
                    <div className="col-span-2 relative">
                        <FormField
                            control={control}
                            name="contract_number_id"
                            type="textfield"
                            label={t('contract_number_id')}
                            placeholder={t('contract_number_id')}
                            required
                            onBlur={handleCheckContractNumber}
                            className="[&_input]:pr-10"
                        />
                        <div className="absolute right-3 top-[22px]">
                            {renderNumberStatus()}
                        </div>
                    </div>

                    {/* Contract Key */}
                    <div className="col-span-2">
                        <FormField
                            control={control}
                            name="contract_key"
                            type="textfield"
                            label={t('contract_key')}
                            placeholder={t('contract_key')}
                        />
                    </div>

                    {/* Contract Value */}
                    <div className="col-span-4">
                        <FormField
                            control={control}
                            decimals={2}
                            name="contract_value"
                            type="number"
                            formatted
                            label={t('contract_value')}
                            placeholder={t('contract_value')}
                        />
                    </div>

                    {/* Contract Duration */}
                    <div className="col-span-4">
                        <FormField
                            control={control}
                            name={[
                                'contract_effective_date',
                                'contract_expiry_date',
                            ]}
                            type="daterangepicker"
                            label={t('contract_duration')}
                            placeholder={t('contract_duration')}
                            numberOfMonths={2}
                        />
                    </div>

                    {/* Attachments - keep vnetwork-ui FormItem for files type */}
                    <div className="col-span-4">
                        <FormItem
                            control={control}
                            name="attachments"
                            type="files"
                            renderFileItem={(file: File | Attachment) =>
                                renderFileItem(file, onDelete)
                            }
                            label={t('attachments')}
                            placeholder={t('attachments')}
                        />
                    </div>

                    {/* Note */}
                    <div className="col-span-4">
                        <FormField
                            control={control}
                            name="contract_note"
                            type="textarea"
                            label={t('contract_note')}
                            placeholder={t('contract_note')}
                        />
                    </div>
                </div>

                {/* Actions */}
                <div className="flex justify-end gap-2 mt-4">
                    <Button
                        variant="outlined"
                        onClick={onClose}
                        disabled={isLoading}
                    >
                        {t('cancel')}
                    </Button>
                    <Button
                        type="submit"
                        variant="contained"
                        loading={isLoading}
                        disabled={isLoading || numberExists === true}
                    >
                        {t(`${mode}-partner-contract`)}
                    </Button>
                </div>
            </form>
        </Dialog>
    )
}

export default CreatePartnerContractModal
